import type {
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';

import type { ChartUnit } from '../../../types/chart-unit.js';
import type { Formatter } from '../../../types/formatter.js';
import type { TickValueFormatter } from '../../../types/tick-value.js';
import { isLogScale, isSymlogScale } from '../../../utils/detect-scale.js';
import {
  tickValueFormatter,
  formatValue,
} from '../../../utils/format-value.js';

const sanitizeEpsilonValue = (value: number) =>
  value === Number.EPSILON ? 0 : value;

/**
 * Gets y-axis tick formatter depending on the type of scale.
 *
 * As the log scale doesn't support zero or negative values,
 * the smallest number is EPSILON, and it must be sanitized into a 0
 * to represent better human-readable ticks.
 *
 * @param scale - axis scale
 * @param unit - axis unit
 * @param formatter - axis formatter
 * @param numTicks - proposed number of ticks
 * @param precision - tick values precision
 *
 * @returns formatter
 */
export function getYAxisTickFormatter(
  scale:
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>
    | ScaleLinear<number, number>
    | ScaleTime<number, number>,
  unit?: ChartUnit,
  formatter?: Formatter,
  numTicks?: number,
  precision?: number,
): TickValueFormatter {
  const valueFormatter = formatter ?? formatValue;
  const logFormatter =
    (scale: ScaleLogarithmic<number, number> | ScaleSymLog<number, number>) =>
    (value: number) => {
      // d3's default formatter for log scale automatically formats non-major values to empty string
      const isMajorValue = scale.tickFormat(numTicks)(value) !== '';
      const nonEpsilonValue = sanitizeEpsilonValue(value);

      return isMajorValue
        ? valueFormatter(nonEpsilonValue, unit, precision)
        : '';
    };
  const linearFormatter = (value: number) =>
    valueFormatter(value, unit, precision);

  const yAxisTickFormatter =
    isLogScale(scale) || isSymlogScale(scale)
      ? logFormatter(scale)
      : linearFormatter;

  return tickValueFormatter(yAxisTickFormatter, unit);
}
